import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button, TextField, Container, Typography, Box, Card, CardContent, Snackbar, Alert, Chip, Grid } from '@mui/material';
import { Emergency, Computer, Cloud, Security, BugReport, Phone } from '@mui/icons-material';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    issueType: '',
    description: '',
    urgencyLevel: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const toggleAudio = () => {
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        audioRef.current.play().catch(console.error);
        setAudioPlaying(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Simulate email sending
      const emailContent = {
        to_email: 'admin@9-juanjuan.com',
        from_name: formData.name,
        company: formData.company,
        email: formData.email,
        issue_type: formData.issueType,
        description: formData.description,
        urgency: formData.urgencyLevel
      };
      
      console.log('IT Emergency Report:', emailContent);
      
      setAlertMessage('🚀 Emergency ticket submitted! Juan will contact you within the hour!');
      setShowAlert(true);
      
      // Reset form
      setFormData({ name: '', company: '', email: '', issueType: '', description: '', urgencyLevel: '' });
    } catch (error) {
      setAlertMessage('❌ Failed to submit ticket. Please try again!');
      setShowAlert(true);
    }
  };

  return (
    <div className="App">
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box textAlign="center" py={4}>
            {/* Juan's Profile Picture */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="juan-profile"
            >
              <div 
                className="profile-circle"
                onClick={toggleAudio}
                style={{ cursor: 'default' }}
              >
                <img 
                  src="/images/juan-profile.jpg" 
                  alt="Juan - IT Emergency Specialist"
                  className="profile-image"
                  onError={(e) => {
                    // Fallback to computer icon if image doesn't load
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className="profile-fallback" style={{ display: 'none' }}>
                  <Computer sx={{ fontSize: '4rem', color: '#fff' }} />
                </div>
              </div>
              
              {/* Hidden audio element */}
              <audio
                ref={audioRef}
                loop
                onPlay={() => setAudioPlaying(true)}
                onPause={() => setAudioPlaying(false)}
              >
                <source src="/9-juanjuan.mp3" type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Typography variant="h1" component="h1" gutterBottom className="main-title">
                BETTER CALL JUAN
              </Typography>
              <Typography variant="h2" component="h2" className="subtitle">
                IT & KUBERNETES EMERGENCY HOTLINE
              </Typography>
              <Typography variant="h5" className="tagline" sx={{ mt: 2, mb: 3 }}>
                <Phone sx={{ verticalAlign: 'middle', mr: 1 }} />
                "When your cluster's down, don't frown - Call Juan!"
              </Typography>
            </motion.div>
          </Box>
        </motion.div>

        {/* Floating Tech Icons Background */}
        <div className="tech-background">
          {[Computer, Cloud, Security, BugReport].map((Icon, i) => (
            <motion.div
              key={i}
              className={`floating-icon icon-${i + 1}`}
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 4 + i * 0.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Icon sx={{ fontSize: '2rem', opacity: 0.2, color: '#fff' }} />
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" textAlign="center" className="services-title" sx={{ mb: 3 }}>
              EMERGENCY IT SERVICES
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid>
                <Chip icon={<Cloud />} label="Kubernetes Rescue" className="service-chip" />
              </Grid>
              <Grid>
                <Chip icon={<Computer />} label="System Recovery" className="service-chip" />
              </Grid>
              <Grid>
                <Chip icon={<Security />} label="Security Breach" className="service-chip" />
              </Grid>
              <Grid>
                <Chip icon={<BugReport />} label="Critical Bugs" className="service-chip" />
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Emergency Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <Card className="emergency-card" sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h4" gutterBottom className="form-title" textAlign="center">
                🚨 SUBMIT EMERGENCY IT TICKET 🚨
              </Typography>
              <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
                Cluster down? Pods failing? Security breach? Don't panic! Juan's got your back with 24/7 emergency IT support.
              </Typography>
              
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                  />
                  
                  <TextField
                    fullWidth
                    label="Company/Organization"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="Which company needs our help?"
                  />
                  
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="your.email@company.com"
                  />
                  
                  <TextField
                    fullWidth
                    label="Issue Type"
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="Kubernetes, Docker, Security, Network, etc."
                  />
                  
                  <TextField
                    fullWidth
                    label="Describe Your IT Emergency"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Tell Juan exactly what's broken and how it's affecting your business..."
                  />
                  
                  <TextField
                    fullWidth
                    label="Urgency Level (1-10)"
                    name="urgencyLevel"
                    value={formData.urgencyLevel}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    placeholder="10 = Production down, 1 = Minor annoyance"
                    type="number"
                    inputProps={{ min: 1, max: 10 }}
                  />
                  
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<Emergency />}
                      className="emergency-button"
                      sx={{ py: 2, fontSize: '1.2rem' }}
                    >
                      CALL JUAN NOW!
                    </Button>
                  </motion.div>
                </Box>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          <Card className="stats-card">
            <CardContent>
              <Typography variant="h5" gutterBottom textAlign="center">
                ⚡ JUAN'S EMERGENCY RESPONSE STATS ⚡
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 2 }}>
                <Box textAlign="center">
                  <Typography variant="h3" className="stat-number">24/7</Typography>
                  <Typography>Always Available</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" className="stat-number">{'< 1HR'}</Typography>
                  <Typography>Response Time</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" className="stat-number">99.9%</Typography>
                  <Typography>Success Rate</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" className="stat-number">∞</Typography>
                  <Typography>Clusters Saved</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer */}
        <Box textAlign="center" py={4}>
          <Typography variant="body2" className="footer-text">
            © 2024 Better Call Juan - Emergency IT Services | Contact: admin@9-juanjuan.com
          </Typography>
          <Typography variant="body2" className="footer-tagline">
            "When your infrastructure fails, Juan never does." 🚀💻
          </Typography>
        </Box>
      </Container>

      {/* Alert Snackbar */}
      <Snackbar 
        open={showAlert} 
        autoHideDuration={6000} 
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowAlert(false)} severity="success">
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default App;
